import { useState } from 'react';
import styled from '@emotion/styled';
import { Tabs } from 'antd';
import { useTranslate } from '@tolgee/react';
import FilterBar from './FilterBar';
import ItemList from './ItemList';
import SelectResult from './SelectResult';
import QuickSelector from './QuickSelector';
import SideStoreSelect from './SideStoreSelect';
import ringPng from 'assets/images/ring.png';

type Mode = 'spu' | 'mult';

const TipContent = ({ title }: { title: string }) => {
  return (
    <div className="relative px-3 py-1 bg-[#EEF6FF] rounded-lg mx-2" title={title}>
      <img className="w-6 h-6 absolute -left-4 top-1/2 -translate-y-1/2" src={ringPng} alt="ring" />
      <div className="max-w-[420px] overflow-hidden text-ellipsis whitespace-nowrap">{title}</div>
    </div>
  );
};

const Implement = ({
  skuDisabled,
  sideStoreEnable,
  defaultFoldSku,
  tooltips,
}: {
  skuDisabled?: boolean;
  sideStoreEnable?: boolean;
  defaultFoldSku?: boolean;
  tooltips?: string;
}) => {
  const { t } = useTranslate();
  const [mode, setMode] = useState<Mode>('spu');
  return (
    <Container>
      <CenterContent>
        <Tabs
          activeKey={mode}
          onChange={setMode as any}
          tabBarExtraContent={tooltips ? <TipContent title={tooltips} /> : undefined}
        >
          <Tabs.TabPane key="spu" tab={t('knowledge.item.select.mode.spu')} />
          <Tabs.TabPane key="mult" tab={t('knowledge.item.select.mode.mult')} />
        </Tabs>
        <section>
          {sideStoreEnable && (
            <aside>
              <SideStoreSelect />
            </aside>
          )}
          {mode === 'spu' ? (
            <SelectorArea skuDisabled={skuDisabled} defaultFoldSku={defaultFoldSku} />
          ) : (
            <QuickSelector />
          )}
        </section>
      </CenterContent>
      <RightSide>
        <SelectResult skuDisabled={skuDisabled} />
      </RightSide>
    </Container>
  );
};

function SelectorArea({
  skuDisabled,
  defaultFoldSku,
}: {
  skuDisabled?: boolean;
  defaultFoldSku?: boolean;
}) {
  return (
    <SelectorAreaContainer>
      <FilterBar />
      <ItemList skuDisabled={skuDisabled} defaultFoldSku={defaultFoldSku} />
    </SelectorAreaContainer>
  );
}

const Container = styled.div`
  max-height: 600px;
  display: flex;
  align-items: stretch;
  height: calc(94vh - 108px);
  overflow: hidden;
`;

const CenterContent = styled.div`
  flex: 0 0 auto;
  width: 800px;
  height: 100%;
  display: flex;
  flex-direction: column;
  border-right: 1px solid var(--fett-border-default);
  > .ant-tabs {
    flex: 0 0 auto;
    .ant-tabs-nav {
      margin-bottom: 0;
    }
    .ant-tabs-nav-wrap {
      padding: 0 18px;
    }
  }
  > section {
    flex: 1 0 0;
    overflow: hidden;
    display: flex;
    > * {
      flex: 1 0 0;
      overflow: hidden;
    }
    > aside {
      flex: 0 0 auto;
      > .ant-tabs {
        height: 100%;
        .ant-tabs-content-holder {
          width: 0;
        }
        .ant-tabs-tab {
          max-width: 130px;
          margin-top: 0 !important;
          padding: 8px 12px;
          white-space: pre-wrap;
          > div {
            max-width: 100%;
          }
        }
      }
    }
  }
`;

const RightSide = styled.div`
  background: var(--fett-erp-card-bg);
  flex: 1 0 0;
  max-width: 500px;
  overflow: hidden;
`;

const SelectorAreaContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

export default Implement;
